import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, FileCheck2, GraduationCap, ShieldCheck, Upload } from "lucide-react";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api, money } from "@/lib/api";

const STEPS = [
  {
    icon: GraduationCap,
    title: "Family enrolls",
    body: "A family enrolls directly with Alabama Church School through their partner-specific enrollment page on the ACS Partner Portal and pays Alabama Church School directly.",
  },
  {
    icon: Upload,
    title: "Partner submits records",
    body: "The educational partner uploads the student's completed annual academic records package.",
  },
  {
    icon: FileCheck2,
    title: "ACS reviews & issues",
    body: "ACS reviews the submitted records and issues the Annual Academic Record & Report Card.",
  },
];

export default function Landing() {
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/public/settings").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-left">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Brand />
          <div className="flex items-center gap-2">
            <Link to="/become-a-partner">
              <Button size="sm" variant="outline" data-testid="header-become-partner-button">
                Partner With ACS
              </Button>
            </Link>
            <Link to="/partner">
              <Button size="sm" data-testid="header-login-button">
                Partner sign in
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="acs-grain border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">
              For Curriculum Providers &amp; Educational Programs
            </p>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] text-slate-900 sm:text-5xl lg:text-6xl">
              You provide the education.
              <br />
              We handle the school records.
            </h1>
            <p className="mt-6 max-w-xl text-base text-slate-600">
              Alabama Church School partners with curriculum providers and educational programs
              that want to focus on teaching while ACS handles family enrollment, annual academic
              records review, and official academic documents.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/become-a-partner">
                <Button size="lg" className="gap-2" data-testid="landing-become-partner-button">
                  Partner With ACS <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <a href="#partner-family-enrollment">
                <Button
                  size="lg"
                  variant="outline"
                  className="gap-2"
                  data-testid="landing-enroll-button"
                >
                  Partner Family Enrollment
                </Button>
              </a>
              <Link to="/partner">
                <Button size="lg" variant="ghost" data-testid="landing-signin-button">
                  Partner sign in
                </Button>
              </Link>
            </div>
            <p className="mt-6 max-w-xl text-xs leading-relaxed text-slate-500">
              Alabama Church School does not provide curriculum, lessons, grading, tutoring, or
              instruction through this portal.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-lg border border-slate-200 shadow-md">
              <img
                src="https://images.pexels.com/photos/9240631/pexels-photo-9240631.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Family learning at home"
                className="h-64 w-full object-cover sm:h-80 lg:h-[22rem]"
              />
            </div>
            {pricing && (
              <Card className="mt-6 border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <h2 className="text-base font-semibold text-slate-900">Current ACS fees</h2>
                  <ul className="mt-4 space-y-3 text-sm text-slate-600">
                    <li className="flex items-center justify-between gap-4">
                      <span>Initial family enrollment</span>
                      <span className="font-semibold text-slate-900">
                        {money(pricing.price_initial_enrollment)}
                      </span>
                    </li>
                    <li className="flex items-center justify-between gap-4">
                      <span>Annual family re-enrollment</span>
                      <span className="font-semibold text-slate-900">
                        {money(pricing.price_annual_reenrollment)}
                      </span>
                    </li>
                    <li className="flex items-center justify-between gap-4">
                      <span>Annual records review (per student)</span>
                      <span className="font-semibold text-slate-900">
                        {money(pricing.price_records_review)}
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>
      </section>

      <section
        id="partner-family-enrollment"
        className="scroll-mt-20 border-b border-slate-200 bg-white"
      >
        <div className="mx-auto max-w-6xl px-5 py-14">
          <h2 className="font-display text-2xl font-bold text-slate-900">
            Partner family enrollment
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-600">
            Families enroll directly with Alabama Church School through their partner-specific
            enrollment page on this portal. Each partner has its own path here, and that page
            automatically associates your family with the correct partner.
          </p>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-600">
            Please use the enrollment link provided by your educational partner. If you do not have
            it, contact your partner directly and they will supply the correct enrollment page.
          </p>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-600">
            Do not have an educational partner yet?{" "}
            <Link
              to="/partners"
              className="font-semibold text-blue-700 underline"
              data-testid="find-partners-link"
            >
              See the participating partners
            </Link>{" "}
            to choose a curriculum and enroll through their page.
          </p>

        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="font-display text-3xl font-bold text-slate-900">How the portal works</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <Card className="h-full border-slate-200 transition-transform duration-200 hover:-translate-y-[2px] hover:shadow-md">
                <CardContent className="p-7">
                  <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-md bg-slate-900">
                    <step.icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-900">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.body}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-14 md:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900">
              For educational partners
            </h2>
            <ul className="mt-5 space-y-3 text-sm text-slate-600">
              {[
                "Secure login scoped to your own students only",
                "Upload the annual academic records package as a PDF",
                "See review status and any ACS correction notes",
                "Download the final ACS document once issued",
              ].map((item) => (
                <li key={item} className="flex gap-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-7">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
            <h3 className="mt-4 text-base font-semibold text-slate-900">Out-of-state notice</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Enrollment and academic documentation provided through Alabama Church School does not
              guarantee compliance with the homeschool, compulsory attendance, private school, or
              other education laws of the student's state or jurisdiction of residence. The parent or
              legal guardian is responsible for determining and meeting all requirements that apply
              where the student resides.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-5 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <span>© {new Date().getFullYear()} Alabama Church School. All rights reserved.</span>
          <span>partners.alabamachurchschool.com</span>
        </div>
      </footer>
    </div>
  );
}
